import React, { useState, useEffect, useRef, useContext, useCallback, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useApp } from "../../context/AppContext";
import { cn } from "../../lib/utils";
import { Check, Settings2, UserCircle2, Search } from "lucide-react";
import { BoardMembersContext, EDIT_HOVER } from "./boardConstants";

// Portal-anchored popover so menus are never clipped by table overflow (fixes subitem dropdowns).
export function Popover({ renderTrigger, children, align = "left", menuWidth = 180, testid, triggerClassName }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState(null);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const place = useCallback(() => {
    if (!anchorRef.current) return;
    const r = anchorRef.current.getBoundingClientRect();
    let left = align === "right" ? r.right - menuWidth : r.left;
    left = Math.max(8, Math.min(left, window.innerWidth - menuWidth - 8));
    const menuH = menuRef.current?.offsetHeight || 0;
    let top = r.bottom + 4;
    if (menuH && top + menuH > window.innerHeight - 8) {
      top = Math.max(8, r.top - menuH - 4); // flip above when it would overflow the bottom
    }
    setPos({ top, left });
  }, [align, menuWidth]);
  useLayoutEffect(() => {
    if (!open) return;
    place();
    const id = requestAnimationFrame(place); // re-place once the menu has measurable height (for flip)
    return () => cancelAnimationFrame(id);
  }, [open, place]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (anchorRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onMove = () => place();
    document.addEventListener("mousedown", onDown);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => { document.removeEventListener("mousedown", onDown); window.removeEventListener("scroll", onMove, true); window.removeEventListener("resize", onMove); };
  }, [open, place]);
  return (
    <>
      <div ref={anchorRef} data-testid={testid} onClick={() => setOpen((o) => !o)} className={triggerClassName}>{renderTrigger(open)}</div>
      {open && pos && createPortal(
        <div ref={menuRef} style={{ position: "fixed", top: pos.top, left: pos.left, zIndex: 9999, minWidth: menuWidth }}
          className="rounded-lg border bg-surface shadow-2xl animate-in fade-in zoom-in-95 duration-100">
          {typeof children === "function" ? children(() => setOpen(false)) : children}
        </div>, document.body)}
    </>
  );
}

function initialsOf(p) {
  return (p?.name || p?.email || "?").trim().split(/\s+/).map((s) => s[0]).join("").slice(0, 2).toUpperCase();
}

export function Avatar({ person, size = 22 }) {
  if (person?.picture) {
    return <img src={person.picture} alt={person.name || ""} className="rounded-full object-cover shrink-0" style={{ width: size, height: size }} />;
  }
  return (
    <span className="rounded-full bg-text-primary text-background font-semibold flex items-center justify-center shrink-0"
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}>{initialsOf(person)}</span>
  );
}

// Avatar that shows a portal info-card on hover.
export function HoverAvatar({ person, size = 22, testid }) {
  const ref = useRef(null);
  const [card, setCard] = useState(null);
  const { t } = useApp();
  const show = () => { const r = ref.current?.getBoundingClientRect(); if (r) setCard({ top: r.bottom + 6, left: Math.min(r.left, window.innerWidth - 248) }); };
  return (
    <span ref={ref} className="relative inline-flex" onMouseEnter={show} onMouseLeave={() => setCard(null)} data-testid={testid}>
      <span className="ring-2 ring-background rounded-full inline-flex"><Avatar person={person} size={size} /></span>
      {card && createPortal(
        <div style={{ position: "fixed", top: card.top, left: card.left, zIndex: 10000, width: 240 }}
          className="rounded-xl border bg-surface shadow-2xl p-3 animate-in fade-in zoom-in-95 duration-100" data-testid={`${testid}-card`}>
          <div className="flex items-center gap-3">
            <Avatar person={person} size={40} />
            <div className="min-w-0">
              <div className="font-semibold text-sm truncate">{person.name || person.email}</div>
              {person.email && <div className="text-xs text-text-secondary truncate">{person.email}</div>}
            </div>
          </div>
          <div className="mt-2.5 inline-flex items-center gap-1.5 text-[11px] px-2 py-0.5 rounded-full bg-surface-hover text-text-secondary capitalize">
            {person.role === "owner" ? t("role_owner") : person.role === "admin" ? t("role_admin") : t("role_member")}
          </div>
        </div>, document.body)}
    </span>
  );
}

// Multi-owner people picker scoped to the board's entity members.
export function PeopleCell({ value, onChange, testid }) {
  const members = useContext(BoardMembersContext);
  const { t } = useApp();
  const [query, setQuery] = useState("");
  const ids = Array.isArray(value) ? value : [];
  const selected = ids.map((id) => members.find((m) => m.user_id === id)).filter(Boolean);
  const toggle = (id) => onChange(ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]);
  const filtered = members.filter((m) => {
    const q = query.trim().toLowerCase();
    return !q || (m.name || "").toLowerCase().includes(q) || (m.email || "").toLowerCase().includes(q);
  });
  return (
    <Popover testid={testid} menuWidth={248} triggerClassName="cursor-pointer"
      renderTrigger={() => (
        selected.length === 0
          ? <div className="flex items-center justify-center h-7" data-testid={`${testid}-empty`}><UserCircle2 className="w-5 h-5 text-text-secondary/60" /></div>
          : (
            <div className="flex items-center justify-center h-7" data-testid={`${testid}-avatars`}>
              <div className="flex -space-x-1.5">
                {selected.slice(0, 3).map((p) => <HoverAvatar key={p.user_id} person={p} size={22} testid={`${testid}-avatar-${p.user_id}`} />)}
              </div>
              {selected.length > 3 && <span className="ml-1 text-[11px] text-text-secondary">+{selected.length - 3}</span>}
            </div>
          )
      )}>
      {() => (
        <div className="p-2 w-[248px]" data-testid={`${testid}-menu`}>
          <div className="flex items-center gap-2 px-2 py-1.5 rounded-lg bg-surface-hover mb-2">
            <Search className="w-3.5 h-3.5 text-text-secondary" />
            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("board_search_people")} data-testid={`${testid}-search`} className="flex-1 bg-transparent text-sm outline-none" />
          </div>
          <div className="max-h-56 overflow-y-auto space-y-0.5">
            {filtered.length === 0 && <div className="text-xs text-text-secondary px-2 py-3 text-center">{t("board_no_members")}</div>}
            {filtered.map((m) => {
              const on = ids.includes(m.user_id);
              return (
                <button key={m.user_id} onClick={() => toggle(m.user_id)} data-testid={`${testid}-opt-${m.user_id}`}
                  className={cn("w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg text-left transition-colors", on ? "bg-primary/10" : "hover:bg-surface-hover")}>
                  <Avatar person={m} size={26} />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-medium truncate">{m.name || m.email}</div>
                    {m.email && <div className="text-[11px] text-text-secondary truncate">{m.email}</div>}
                  </div>
                  {on && <Check className="w-4 h-4 text-primary shrink-0" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </Popover>
  );
}

// Inline input that commits on blur/Enter and stays synced with external value.
export function CellInput({ value, onCommit, type = "text", testid, className, placeholder = "—" }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => { setV(value ?? ""); }, [value]);
  return (
    <input type={type} data-testid={testid} value={v} placeholder={placeholder}
      onChange={(e) => setV(e.target.value)}
      onBlur={() => onCommit(type === "number" ? (v === "" ? "" : Number(v)) : v)}
      onKeyDown={(e) => e.key === "Enter" && e.currentTarget.blur()}
      className={cn(EDIT_HOVER, className)} />
  );
}

// Item name cell: ellipsis truncation + hover tooltip (only when truncated) + click to edit.
export function ItemName({ value, onSave, testid, startEditing = false, onStartConsumed, className, maxWidth }) {
  const [editing, setEditing] = useState(startEditing);
  const [val, setVal] = useState(value || "");
  const [hover, setHover] = useState(false);
  const [truncated, setTruncated] = useState(false);
  const [tipPos, setTipPos] = useState(null);
  const spanRef = useRef(null);
  useEffect(() => { setVal(value || ""); }, [value]);
  const commit = () => { setEditing(false); if (val.trim() && val !== value) onSave(val.trim()); else setVal(value || ""); };
  const onEnter = () => {
    const el = spanRef.current;
    if (el) {
      const isTrunc = el.scrollWidth > el.clientWidth + 1;
      setTruncated(isTrunc);
      if (isTrunc) {
        const r = el.getBoundingClientRect();
        setTipPos({ left: r.left, top: r.bottom + 6 });
      }
    }
    setHover(true);
  };
  if (editing) {
    return (
      <input autoFocus data-testid={`${testid}-input`} value={val}
        onFocus={(e) => { e.target.select(); if (onStartConsumed) onStartConsumed(); }}
        onChange={(e) => setVal(e.target.value)} onBlur={commit}
        onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setVal(value || ""); setEditing(false); } }}
        style={maxWidth ? { maxWidth } : undefined}
        className={cn("w-full bg-transparent outline-none text-sm font-medium border-b border-primary", className)} />
    );
  }
  return (
    <div className="relative min-w-0 flex-1" style={maxWidth ? { maxWidth } : undefined}>
      <span ref={spanRef} data-testid={testid} onClick={() => setEditing(true)}
        onMouseEnter={onEnter} onMouseLeave={() => setHover(false)}
        className={cn("block truncate cursor-text text-sm font-medium px-1.5 -mx-1.5 hover:bg-surface-hover", EDIT_HOVER, !value && "text-text-secondary", className)}>
        {value || "—"}
      </span>
      {hover && truncated && tipPos && createPortal(
        <div data-testid={`${testid}-tooltip`}
          style={{ position: "fixed", left: tipPos.left, top: tipPos.top, maxWidth: 360 }}
          className="z-[100] px-2.5 py-1.5 rounded-lg bg-text-primary text-background text-xs font-medium shadow-xl whitespace-normal pointer-events-none">
          {value}
        </div>, document.body)}
    </div>
  );
}

function OptionDropdown({ options, value, onChange, testid, onEdit }) {
  const { t } = useApp();
  const sel = (options || []).find((o) => o.id === value);
  return (
    <Popover testid={testid} menuWidth={160}
      renderTrigger={() => (
        <div className="w-full h-7 px-2 rounded-md text-xs font-medium flex items-center justify-center transition-colors cursor-pointer"
          style={sel ? { backgroundColor: sel.color, color: "#fff" } : { color: "var(--text-secondary)" }}>
          {sel ? sel.label : "—"}
        </div>
      )}>
      {(close) => (
        <div className="p-1.5 space-y-1" data-testid={`${testid}-menu`}>
          {(options || []).map((o) => (
            <button key={o.id} data-testid={`${testid}-opt-${o.id}`} onClick={() => { onChange(o.id); close(); }}
              className="w-full text-left px-2 py-1.5 rounded-md text-xs font-medium text-white" style={{ backgroundColor: o.color }}>
              {o.label}
            </button>
          ))}
          <button onClick={() => { onChange(""); close(); }} className="w-full text-left px-2 py-1.5 rounded-md text-xs text-text-secondary hover:bg-surface-hover">— Clear</button>
          {onEdit && (
            <button onClick={() => { close(); onEdit(); }} data-testid={`${testid}-edit`}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 rounded-md text-xs text-text-secondary hover:bg-surface-hover border-t mt-1 pt-2">
              <Settings2 className="w-3.5 h-3.5" /> {t("board_edit_options")}
            </button>
          )}
        </div>
      )}
    </Popover>
  );
}

export function Cell({ col, value, onChange, testid, onEditColumn, inDrawer = false }) {
  if (col.type === "status" || col.type === "priority" || col.type === "dropdown") {
    return <OptionDropdown options={col.settings?.options || []} value={value} onChange={onChange} testid={testid}
      onEdit={onEditColumn ? () => onEditColumn(col) : undefined} />;
  }
  if (col.type === "date") {
    return <input type="date" data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)}
      className={cn("h-7 px-1 bg-transparent text-xs outline-none hover:bg-surface-hover cursor-pointer", EDIT_HOVER, inDrawer ? "w-auto text-left" : "w-full text-center")} />;
  }
  if (col.type === "timeline") {
    const v = value && typeof value === "object" ? value : { start: "", end: "" };
    return (
      <div className="flex items-center gap-1">
        <input type="date" data-testid={`${testid}-start`} value={v.start || ""} onChange={(e) => onChange({ ...v, start: e.target.value })} className={cn("w-1/2 h-7 px-1 bg-transparent text-[11px] text-center outline-none hover:bg-surface-hover", EDIT_HOVER)} />
        <span className="text-text-secondary text-xs">→</span>
        <input type="date" data-testid={`${testid}-end`} value={v.end || ""} onChange={(e) => onChange({ ...v, end: e.target.value })} className={cn("w-1/2 h-7 px-1 bg-transparent text-[11px] text-center outline-none hover:bg-surface-hover", EDIT_HOVER)} />
      </div>
    );
  }
  if (col.type === "number") {
    return <CellInput type="number" value={value} onCommit={onChange} testid={testid} className={cn("h-7 px-2 rounded-md bg-transparent text-xs outline-none hover:bg-surface-hover", inDrawer ? "w-full text-left" : "w-full text-center")} />;
  }
  if (col.type === "checkbox") {
    return (
      <div className={inDrawer ? "" : "flex justify-center"}>
        <button data-testid={testid} onClick={() => onChange(!value)} className="w-5 h-5 rounded border-2 flex items-center justify-center transition-colors"
          style={value ? { backgroundColor: "#22C55E", borderColor: "#22C55E" } : { borderColor: "var(--border-strong)" }}>
          {value && <Check className="w-3 h-3 text-white" />}
        </button>
      </div>
    );
  }
  if (col.type === "progress") {
    const vv = Math.min(100, Math.max(0, Number(value) || 0));
    return (
      <div className="flex items-center gap-2 px-1">
        <div className="flex-1 h-2 rounded-full bg-surface-hover overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${vv}%`, backgroundColor: "#22C55E" }} />
        </div>
        <CellInput type="number" value={vv} onCommit={(n) => onChange(Math.min(100, Math.max(0, Number(n) || 0)))} testid={testid} className="w-10 bg-transparent text-xs text-right outline-none tabular-nums" />
      </div>
    );
  }
  if (col.type === "people") {
    return <PeopleCell value={value} onChange={onChange} testid={testid} />;
  }
  return <CellInput value={value} onCommit={onChange} testid={testid} className="w-full h-7 px-2 rounded-md bg-transparent text-xs outline-none hover:bg-surface-hover" />;
}
